import React, { useEffect, useState } from "react";
import DocxImportBaseModal from "./docx-import-base-modal";
import SectionPreview from "./section-preview";
import { Checkbox, Button, Modal, Input, message } from "antd";
import "../styles/docx-import-modal.scoped.scss";

const getSectionStatus = (importedSection, existingSections) => {
    if (!existingSections) {
        return "new";
    }
    const match = existingSections.find((s) => s.title === importedSection.title);
    if (!match) {
        return "new";
    }
    if (JSON.stringify(match.content) === JSON.stringify(importedSection.content)) {
        return "unchanged";
    }
    return "modified";
};

const statusLabel = {
    new: "New",
    modified: "Modified",
    unchanged: "Unchanged",
};

const DocxImportModal = ({ visible, onCancel, onSave, file, projectId, store, ...props }) => {
    const [previewData, setPreviewData] = useState(null);
    const [docName, setDocName] = useState("");
    const [acceptedSections, setAcceptedSections] = useState({});
    const [previewIdx, setPreviewIdx] = useState(null);
    const [renameModal, setRenameModal] = useState({ open: false, idx: null, value: "" });
    const [saving, setSaving] = useState(false);
    const [unnumberedSections, setUnnumberedSections] = useState({});
    const [includeComments, setIncludeComments] = useState(true);

    useEffect(() => {
        if (file && projectId && store) {
            store
                .uploadAndPreview({ projectId, file })
                .then((data) => {
                    if (data && data.sections && data.sections.length > 0) {
                        setPreviewData(data);
                        setAcceptedSections(
                            data.sections.reduce((acc, _, idx) => ({ ...acc, [idx]: true }), {}),
                        );
                        const initialUnnumbered = data.sections.reduce((acc, section, idx) => {
                            if (section.hasOriginalNumber === false) {
                                acc[idx] = true;
                            }
                            return acc;
                        }, {});
                        setUnnumberedSections(initialUnnumbered);
                        setDocName("");
                    } else {
                        onCancel();
                    }
                })
                .catch((error) => {
                    onCancel();
                });
        }
    }, [file, projectId, store, onCancel]);

    const isChildOf = (childPos, parentPos) => {
        if (!childPos || !parentPos) {
            return false;
        }
        return childPos.startsWith(parentPos + "/");
    };

    const getChildIndices = (parentIdx) => {
        if (!previewData || !previewData.sections) {
            return [];
        }
        const parentPos = previewData.sections[parentIdx]?.pos;
        if (!parentPos) {
            return [];
        }
        return previewData.sections
            .map((section, idx) => ({ section, idx }))
            .filter(({ section }) => isChildOf(section.pos, parentPos))
            .map(({ idx }) => idx);
    };

    const handleSectionToggle = (idx) => {
        if (!previewData || !previewData.sections) {
            return;
        }
        const newAccepted = !acceptedSections[idx];
        const updates = { [idx]: newAccepted };

        getChildIndices(idx).forEach((childIdx) => {
            updates[childIdx] = newAccepted;
        });

        setAcceptedSections({
            ...acceptedSections,
            ...updates,
        });
    };

    const handleToggleAllNumbers = (include) => {
        if (!previewData || !previewData.sections) {
            return;
        }
        if (include) {
            setUnnumberedSections({});
        } else {
            const allUnnumbered = previewData.sections.reduce(
                (acc, _, idx) => ({ ...acc, [idx]: true }),
                {},
            );
            setUnnumberedSections(allUnnumbered);
        }
    };

    const allNumbersIncluded =
        previewData && previewData.sections
            ? !previewData.sections.some((_, idx) => unnumberedSections[idx])
            : true;

    const allSectionsAccepted = previewData
        ? Object.values(acceptedSections || {}).some(Boolean)
        : false;
    const canSave = docName && allSectionsAccepted;

    const calculateDynamicNumber = (targetIdx) => {
        if (!previewData || !previewData.sections) {
            return null;
        }

        const section = previewData.sections[targetIdx];
        if (!section) {
            return null;
        }

        const acceptedNumbered = [];
        for (let i = 0; i < previewData.sections.length; i++) {
            const s = previewData.sections[i];
            if (acceptedSections[i] && !unnumberedSections[i]) {
                const originalDepth = s.pos ? s.pos.split("/").length : 1;
                acceptedNumbered.push({ idx: i, originalDepth });
            }
        }

        const withEffectiveDepths = [];
        for (let i = 0; i < acceptedNumbered.length; i++) {
            const item = acceptedNumbered[i];
            let effectiveDepth = 1;

            if (i > 0) {
                const prevItem = withEffectiveDepths[i - 1];
                const depthDiff = item.originalDepth - acceptedNumbered[i - 1].originalDepth;

                if (depthDiff > 0) {
                    effectiveDepth = prevItem.effectiveDepth + 1;
                } else if (depthDiff === 0) {
                    effectiveDepth = prevItem.effectiveDepth;
                } else {
                    effectiveDepth = Math.max(1, prevItem.effectiveDepth + depthDiff);
                }
            }

            withEffectiveDepths.push({ ...item, effectiveDepth });
        }

        const counters = [0, 0, 0, 0, 0, 0, 0];

        for (const item of withEffectiveDepths) {
            const level = item.effectiveDepth;

            for (let d = level + 1; d <= 6; d++) {
                counters[d] = 0;
            }
            counters[level]++;

            if (item.idx === targetIdx) {
                const nums = [];
                for (let d = 1; d <= level; d++) {
                    nums.push(counters[d] || 1);
                }
                return nums.join(".") + ".";
            }
        }

        return null;
    };

    const renderSection = (section, idx) => {
        if (!previewData || !previewData.sections) {
            return null;
        }
        const status = getSectionStatus(section, props.existingSections || []);

        let displayNumber = null;
        if (acceptedSections[idx] && !unnumberedSections[idx]) {
            displayNumber = calculateDynamicNumber(idx);
        }
        const isUnnumbered = !!unnumberedSections[idx];
        const showPreviewBtn = status === "modified" || status === "new";
        const isDisabled = !acceptedSections[idx];
        // Indent by hierarchy depth so the preview shows the parent/child tree.
        const depth = section.pos ? section.pos.split("/").filter(Boolean).length : 1;
        return (
            <div
                className={`section-list-item ${isDisabled ? "disabled" : ""}`}
                id={`section-item-${idx}`}
                style={{ marginLeft: Math.max(0, depth - 1) * 24 }}
            >
                <div className="section-header">
                    <div className="section-header-left">
                        <Checkbox
                            checked={!!acceptedSections[idx]}
                            onChange={() => handleSectionToggle(idx)}
                            className="checkbox"
                        />
                        <b>{displayNumber ? `${displayNumber} ${section.title}` : section.title}</b>
                        {acceptedSections[idx] && (
                            <Button
                                type="text"
                                size="small"
                                className="toggle-number-btn"
                                onClick={() =>
                                    setUnnumberedSections({
                                        ...unnumberedSections,
                                        [idx]: !unnumberedSections[idx],
                                    })
                                }
                            >
                                {isUnnumbered ? "Add #" : "Remove #"}
                            </Button>
                        )}
                    </div>
                    <div className="section-header-right">
                        <span className={`status status--${status}`}>{statusLabel[status]}</span>
                        {showPreviewBtn && (
                            <Button
                                type="button"
                                className="diff-btn"
                                disabled={isDisabled}
                                onClick={() => {
                                    if (previewData && previewData.sections && !isDisabled) {
                                        setPreviewIdx(previewIdx === idx ? null : idx);
                                    }
                                }}
                            >
                                {previewIdx === idx ? "Hide Preview" : "Preview"}
                            </Button>
                        )}
                    </div>
                </div>
                {previewIdx === idx && <SectionPreview section={section} />}
            </div>
        );
    };

    useEffect(() => {
        if (previewIdx !== null) {
            const el = document.getElementById(`section-item-${previewIdx}`);
            if (el && typeof el.scrollIntoView === "function") {
                el.scrollIntoView({ behavior: "auto", block: "start" });
            }
        }
    }, [previewIdx]);

    const handleRename = () => {
        if (!previewData || !previewData.sections) {
            return;
        }
        if (!renameModal.value.trim()) {
            message.error("Section name cannot be empty.");
            return;
        }
        const newSections = [...previewData.sections];
        newSections[renameModal.idx] = {
            ...newSections[renameModal.idx],
            title: renameModal.value.trim(),
        };
        setPreviewData({ ...previewData, sections: newSections });
        setRenameModal({ open: false, idx: null, value: "" });
    };

    const handleSave = async () => {
        if (!previewData || !previewData.sections) {
            return;
        }
        try {
            setSaving(true);
            if (onSave) {
                const sectionsToSave = previewData.sections
                    .filter((_, idx) => acceptedSections[idx])
                    .map((section, idx) => {
                        const originalIdx = previewData.sections.findIndex((s) => s === section);
                        if (unnumberedSections[originalIdx]) {
                            return { ...section, displayPos: null };
                        }
                        return section;
                    });
                await onSave({
                    docName,
                    sections: sectionsToSave,
                    comments: includeComments ? previewData.comments || [] : [],
                });
            }
        } finally {
            setSaving(false);
        }
    };

    if (!previewData || !previewData.sections) {
        return null;
    }

    return (
        <>
            <DocxImportBaseModal
                visible={visible}
                onCancel={onCancel}
                onSave={handleSave}
                docName={docName}
                setDocName={setDocName}
                acceptedSections={acceptedSections}
                setAcceptedSections={setAcceptedSections}
                previewData={previewData}
                canSave={canSave}
                isLoading={saving || (store && store.loading)}
                renderSection={renderSection}
                includeComments={includeComments}
                setIncludeComments={setIncludeComments}
                includeNumbers={allNumbersIncluded}
                onToggleAllNumbers={handleToggleAllNumbers}
            />
            <Modal
                visible={renameModal.open}
                title="Rename Section"
                onOk={handleRename}
                onCancel={() => setRenameModal({ open: false, idx: null, value: "" })}
                okText="Rename"
                cancelText="Cancel"
            >
                <p>A section with this name already exists. Please provide a new name:</p>
                <Input
                    value={renameModal.value}
                    onChange={(e) => setRenameModal({ ...renameModal, value: e.target.value })}
                    autoFocus
                />
            </Modal>
        </>
    );
};

export default DocxImportModal;
